// ChoosePlanAndPay.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { fetchSubscriptionPackages } from "../actions/userActions";
import { useNavigate, useParams } from "react-router-dom";
import StripeCreatePayMethod from "../components/StripeCreatePayMethod";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleLeft, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import {
  resetCreatePaymentIntent,
  resetConfirmSubscription,
} from "../actions/restaurantActions";
import LoaderComponent from "../components/LoaderComponent";
import styled, { css } from "styled-components";
import {
  Container,
  CardHeader,
  CardHeaderTitle,
} from "../components/DefaultImports";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { createUpgradeIntent } from "../actions/paymentActions";

/* --------- CONFIG: how many features to preview on cards/details --------- */
// number (e.g., 5) to preview; set to null to show ALL (and hide “Ver mais”)
const FEATURE_PREVIEW_COUNT = null;

/* ---------- Paleta de cores cíclica ---------- */
const palette = ["#9FBBC1", "#EE754C", "#99B0AD"];
const getPlanColor = (subscription_number) =>
  palette[(subscription_number - 1) % palette.length];

/* ---------- Styled-components ---------- */
const ButtonPlans = styled.div`
  padding: 10px 24px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  background-color: ${(props) => (props.isSelected ? "#EE754C" : "#ffffff")};
  color: ${(props) => (props.isSelected ? "#ffffff" : "#EE754C")};
  border: 2px solid #ee754c;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  margin: 5px;
  box-shadow: ${(props) =>
    props.isSelected ? "0 6px 12px rgba(238,117,76,0.2)" : "none"};

  &:hover {
    background-color: #ee561b;
    color: #ffffff;
  }
`;

const PlanCard = styled(Card)`
  border-radius: 20px;
  border: 1px solid #dee2e6;
  background-color: #ffffff;
  padding: 32px 24px;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  min-height: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-5px);
  }

  ${(p) =>
    p.$selected &&
    css`
      border-color: #ee754c;
      box-shadow: 0 6px 16px rgba(238, 117, 76, 0.18);
    `}
`;

const ActiveBadge = styled.div`
  display: inline-block;
  background: #28a745;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  margin-bottom: 8px;
`;

const PaymentPlanCard = styled(PlanCard)`
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 28px;
`;

const PlanName = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: ${({ color }) => `${color || "#333"} !important`};
  margin-bottom: 8px;
`;

const PlanDescription = styled.p`
  font-size: 14px;
  color: #444;
  margin-bottom: 24px;
`;

const PlanPrice = styled.div`
  font-size: 42px;
  font-weight: bold;
  color: ${({ color }) => color || "#333"};
`;

const PerMonth = styled.div`
  font-size: 14px;
  color: #99b0ad;
  margin-bottom: 20px;
`;

const SubscribeButton = styled(Button)`
  background-color: ${({ color }) => color || "#ee754c"};
  border: none;
  border-radius: 999px;
  padding: 10px 28px;
  font-weight: 700;
  font-size: 14px;
  margin: 16px auto 24px;
  &:hover {
    background-color: #ee561b;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding-left: 0;
  text-align: left;
  margin-top: 10px;

  li {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #333;
    margin-bottom: 8px;
    font-family: "Kanit", sans-serif;

    svg {
      color: ${({ iconColor }) => iconColor || "#ee754c"};
      margin-right: 10px;
      font-size: 14px;
    }
  }
`;

const PriceLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
`;

const DiscountNote = styled.div`
  font-size: 13px;
  color: #28a745;
  font-style: italic;
  margin-top: -10px;
  margin-bottom: 12px;
`;

const PaymentTitle = styled.h3`
  font-weight: 700;
  font-size: 20px;
  margin-bottom: 16px;
  color: #333;
  text-align: center;
`;

const ViewMore = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.color || "#ee754c"};
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-top: 6px;

  &:hover {
    text-decoration: underline;
  }
`;

const BackButton = styled(Button)`
  float: right;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 8px;
  background-color: transparent;
  border: 1px solid #ee754c;
  color: #ee754c;
  transition: all 0.3s ease;

  svg {
    margin-right: 6px;
  }

  &:hover {
    background-color: #e5611b;
    color: #ffffff;
    border-color: #e5611b;
  }
`;

const WarningInline = styled.div`
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.35;
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 8px;
  padding: 8px 10px;
  display: inline-block;
  text-align: left;
`;

const OfferItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 10px;
  margin-bottom: 10px;
  color: #333;
  font-size: 14px;

  .icon {
    margin-top: 3px;
    color: ${({ iconColor }) => iconColor || "#ee754c"};
  }
  .text {
    line-height: 1.25;
  }
  .title {
    font-weight: 700;
    margin: 0;
  }
  .desc {
    margin: 2px 0 0;
    color: #4a4a4a;
  }
`;

/* ---------- Helpers (semicolon-only split) ---------- */
const splitOffersSafely = (offersString) => {
  if (!offersString) return [];
  return offersString
    .split(/\s*;\s*/g) // <-- ONLY semicolons
    .map((s) => s.trim())
    .filter(Boolean);
};

const splitTitleAndDesc = (item) => {
  const idx = item.indexOf(":");
  if (idx === -1) return { title: item.trim(), desc: "" };
  return { title: item.slice(0, idx).trim(), desc: item.slice(idx + 1).trim() };
};

/* ---------- Componente principal ---------- */
function ChoosePlanAndPay({ showHeader = true }) {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  /* Redux selectors */
  const { getSubscriptionPackages } = useSelector(
    (state) => state.subscriptionPackagesStore
  );
  const { isLoadingConfirmSubscription } = useSelector(
    (state) => state.paymentIntent
  );
  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails || {};

  const [activeOrTrial, setActiveOrTrial] = useState(false);
  const [activeSubNumber, setActiveSubNumber] = useState(0);
  const [cadence, setCadence] = useState(null);
  const [subEndIso, setSubEndIso] = useState(null);

  const invoicesState = useSelector((s) => s.payments);
  const { upgradePaymentIntent } = invoicesState;
  /* Estado local */
  const [monthlySubscriptions, setMonthlySubscriptions] = useState([]);
  const [yearlySubscriptions, setYearlySubscriptions] = useState([]);
  const [showMonthly, setShowMonthly] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [step, setStep] = useState("choose_subscription");
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [_isConfirmationModal, _setIsConfirmationModal] = useState(false);

  useEffect(() => {
    const status = restaurants?.subscription_status || "";
    setActiveOrTrial(["active", "trialing"].includes(status));

    setActiveSubNumber(Number(restaurants?.sub_lvl ?? 0));

    setCadence(restaurants?.yearly_or_monthly ?? null);

    setSubEndIso(restaurants?.subscription_end_date ?? null);
  }, [
    restaurants?.subscription_status,
    restaurants?.sub_lvl,
    restaurants?.yearly_or_monthly,
    restaurants?.subscription_end_date,
  ]);

  const onConfirmation = () => {
    navigate(`/reservationfromrestaurant/${params.restaurant_id}/null/all`);
  };

  useEffect(() => {
    if (step === "choose_subscription") dispatch(resetCreatePaymentIntent());
  }, [step, dispatch]);

  useEffect(() => {
    dispatch(fetchSubscriptionPackages());
    dispatch(resetConfirmSubscription());
  }, [dispatch]);

  useEffect(() => {
    if (!getSubscriptionPackages) return;

    const packages = [...getSubscriptionPackages].sort(
      (a, b) => a.subscription_number - b.subscription_number
    );

    const monthly = packages.filter(
      (p) => Number(p.number_of_days) > 0 && Number(p.number_of_days) < 90
    );
    const yearly = packages.filter((p) => Number(p.number_of_days) >= 300);

    setMonthlySubscriptions(monthly);
    setYearlySubscriptions(yearly);
  }, [getSubscriptionPackages]);

  useEffect(() => {
    if (cadence === "monthly") setShowMonthly(true);
    else if (cadence === "yearly") setShowMonthly(false);
  }, [cadence]);

  const handleSelectSubscription = (subscription) => {
    const isCurrent =
      activeOrTrial &&
      subscription.subscription_number === Number(activeSubNumber);

    if (isCurrent) return;

    if (activeOrTrial && restaurants?.id) {
      dispatch(
        createUpgradeIntent({
          restaurantId: restaurants.id,
          body: { new_subscription_number: subscription.subscription_number },
        })
      );
    }
    setSelectedSubscription(subscription);
    setStep("payment");
  };

  const handleSetConfirmation = useCallback((val) => {
    _setIsConfirmationModal(val);
  }, []);

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return isNaN(d) ? "" : d.toLocaleDateString("pt-PT");
    } catch {
      return "";
    }
  };

  const isSelectedYearToMonth =
    cadence === "yearly" && selectedSubscription?.number_of_days === 30;

  const isSelectedDowngrade =
    activeOrTrial &&
    selectedSubscription &&
    Number(selectedSubscription.subscription_number) < activeSubNumber;

  const displayAmount =
    activeOrTrial &&
    !isSelectedDowngrade &&
    !isSelectedYearToMonth &&
    upgradePaymentIntent?.amount_cents != null
      ? (upgradePaymentIntent.amount_cents * 0.01).toFixed(2)
      : selectedSubscription?.total_value ?? 0;

  const renderPacks = (subscription) => {
    const color = getPlanColor(subscription.subscription_number);
    const isCurrent =
      activeOrTrial &&
      subscription.subscription_number === Number(activeSubNumber);

    const allOffers = splitOffersSafely(subscription.offers || "");
    const shortOffers = FEATURE_PREVIEW_COUNT
      ? allOffers.slice(0, FEATURE_PREVIEW_COUNT)
      : allOffers;

    return (
      <Col
        sm={12}
        md={4}
        key={subscription.subscription_number}
        className="mb-4 d-flex"
      >
        <PlanCard $selected={isCurrent}>
          {isCurrent && <ActiveBadge>Plano atual</ActiveBadge>}

          <PlanName color={color}>{subscription.name}</PlanName>

          <PlanDescription>{subscription.description}</PlanDescription>

          <PriceLine>
            <PlanPrice color={color}>{subscription.monthly_value} €</PlanPrice>
            <PerMonth>/ mês</PerMonth>
          </PriceLine>

          {subscription.number_of_days === 365 && (
            <DiscountNote>
              *Inclui 20% de desconto em relação ao mensal
            </DiscountNote>
          )}

          <SubscribeButton
            color={color}
            onClick={() => handleSelectSubscription(subscription)}
            disabled={isCurrent}
          >
            {isCurrent ? "Plano atual" : "Subscrever"}
          </SubscribeButton>

          {shortOffers.length > 0 && (
            <FeatureList as="ul" iconColor={color}>
              {shortOffers.map((raw, idx) => {
                const { title, desc } = splitTitleAndDesc(raw);
                return (
                  <OfferItem key={idx} iconColor={color}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faCircleCheck} />
                    </span>
                    <div className="text">
                      <div className="title">{title}</div>
                      {desc && <div className="desc">{desc}</div>}
                    </div>
                  </OfferItem>
                );
              })}
            </FeatureList>
          )}
        </PlanCard>
      </Col>
    );
  };

  return (
    <Container>
      {step === "choose_subscription" ? (
        <Row>
          {showHeader && (
            <CardHeader>
              <CardHeaderTitle>Plano</CardHeaderTitle>
            </CardHeader>
          )}
          {activeOrTrial && restaurants?.id && (
            <Col xs={12}>
              <BackButton
                onClick={() =>
                  navigate(`/subscription_management/${restaurants.id}`)
                }
              >
                <FontAwesomeIcon icon={faCircleLeft} />
                Gerir plano
              </BackButton>
            </Col>
          )}

          <Col xs={12} className="d-flex justify-content-center mb-4 gap-2">
            <ButtonPlans
              isSelected={showMonthly}
              onClick={() => setShowMonthly(true)}
            >
              Mensal
            </ButtonPlans>
            <ButtonPlans
              isSelected={!showMonthly}
              onClick={() => setShowMonthly(false)}
            >
              Anual
            </ButtonPlans>
          </Col>

          <Col xs={12}>
            <Row>
              {(showMonthly ? monthlySubscriptions : yearlySubscriptions).map(
                (sub) => renderPacks(sub)
              )}
            </Row>
          </Col>
        </Row>
      ) : !isLoadingConfirmSubscription ? (
        <>
          {showHeader && (
            <CardHeader>
              <CardHeaderTitle>Plano</CardHeaderTitle>
            </CardHeader>
          )}

          <Row className="mb-2">
            <Col xs={12}>
              <BackButton onClick={() => setStep("choose_subscription")}>
                <FontAwesomeIcon icon={faCircleLeft} />
                Voltar a gerir
              </BackButton>
            </Col>
          </Row>

          <Row className="align-items-start">
            <Col xs={12} md={6} className="mb-4">
              {selectedSubscription && (
                <PaymentPlanCard>
                  {(() => {
                    const color = getPlanColor(
                      selectedSubscription.subscription_number
                    );
                    const endDate = subEndIso ? fmtDate(subEndIso) : null;

                    const fullOffers = splitOffersSafely(
                      selectedSubscription.offers || ""
                    );
                    const previewOffers = FEATURE_PREVIEW_COUNT
                      ? fullOffers.slice(0, FEATURE_PREVIEW_COUNT)
                      : fullOffers;

                    const canToggle =
                      FEATURE_PREVIEW_COUNT &&
                      fullOffers.length > FEATURE_PREVIEW_COUNT;

                    return (
                      <>
                        <PaymentTitle>Pagamento</PaymentTitle>
                        <PlanName color={color}>
                          {selectedSubscription.name}
                        </PlanName>
                        <PlanDescription>
                          Plano{" "}
                          {selectedSubscription.number_of_days === 365
                            ? "anual"
                            : "mensal"}{" "}
                          com funcionalidades essenciais para o seu restaurante.
                        </PlanDescription>
                        <PriceLine>
                          <PlanPrice color={color}>{displayAmount} €</PlanPrice>
                          {selectedSubscription.number_of_days === 365 && (
                            <PerMonth>
                              {selectedSubscription.monthly_value} € / mês
                            </PerMonth>
                          )}
                        </PriceLine>

                        {(isSelectedYearToMonth || isSelectedDowngrade) && (
                          <WarningInline>
                            {isSelectedYearToMonth ? (
                              <>
                                Está a alterar de <b>anual</b> para{" "}
                                <b>mensal</b>. O novo preço mensal será aplicado{" "}
                                {endDate ? (
                                  <>
                                    no fim do período atual (<b>{endDate}</b>).
                                  </>
                                ) : (
                                  <>no fim do período atual.</>
                                )}{" "}
                                Não será cobrado agora, mas pode atualizar o
                                método de pagamento abaixo.
                              </>
                            ) : (
                              <>
                                Está a reduzir o plano. A alteração entra em
                                vigor no fim do período atual{" "}
                                {endDate && (
                                  <>
                                    (<b>{endDate}</b>)
                                  </>
                                )}
                                . Não será cobrado agora, mas pode atualizar o
                                método de pagamento abaixo.
                              </>
                            )}
                          </WarningInline>
                        )}

                        {selectedSubscription.number_of_days === 365 && (
                          <DiscountNote>
                            *Inclui 20% de desconto em relação ao mensal
                          </DiscountNote>
                        )}

                        {!showMoreDetails ? (
                          <>
                            <FeatureList as="ul" iconColor={color}>
                              {previewOffers.map((raw, idx) => {
                                const { title, desc } = splitTitleAndDesc(raw);
                                return (
                                  <OfferItem key={idx} iconColor={color}>
                                    <span className="icon">
                                      <FontAwesomeIcon icon={faCircleCheck} />
                                    </span>
                                    <div className="text">
                                      <div className="title">{title}</div>
                                      {desc && (
                                        <div className="desc">{desc}</div>
                                      )}
                                    </div>
                                  </OfferItem>
                                );
                              })}
                            </FeatureList>
                            {canToggle && (
                              <ViewMore
                                color={color}
                                onClick={() => setShowMoreDetails(true)}
                              >
                                Ver mais
                              </ViewMore>
                            )}
                          </>
                        ) : (
                          <>
                            <FeatureList as="ul" iconColor={color}>
                              {fullOffers.map((raw, idx) => {
                                const { title, desc } = splitTitleAndDesc(raw);
                                return (
                                  <OfferItem key={idx} iconColor={color}>
                                    <span className="icon">
                                      <FontAwesomeIcon icon={faCircleCheck} />
                                    </span>
                                    <div className="text">
                                      <div className="title">{title}</div>
                                      {desc && (
                                        <div className="desc">{desc}</div>
                                      )}
                                    </div>
                                  </OfferItem>
                                );
                              })}
                            </FeatureList>
                            {canToggle && (
                              <ViewMore
                                color={color}
                                onClick={() => setShowMoreDetails(false)}
                              >
                                Ver menos
                              </ViewMore>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </PaymentPlanCard>
              )}
            </Col>

            <Col xs={12} md={6} className="mb-4">
              {selectedSubscription && (
                <>
                  <StripeCreatePayMethod
                    subscription_number={
                      selectedSubscription.subscription_number
                    }
                    restaurant_id={params.restaurant_id}
                    handleSetConfirmation={handleSetConfirmation}
                    isUpdate={activeOrTrial}
                  />
                  <p
                    style={{
                      textAlign: "center",
                      color: "#555",
                      fontSize: "12px",
                      marginTop: "12px",
                    }}
                  >
                    Todos os planos são renovados automaticamente.
                    <br />
                    Pode cancelar a qualquer momento.
                  </p>
                </>
              )}
            </Col>
          </Row>
        </>
      ) : (
        <LoaderComponent activate={isLoadingConfirmSubscription} />
      )}

      <ConfirmationModal
        show={_isConfirmationModal}
        onHide={() => _setIsConfirmationModal(false)}
        onConfirm={onConfirmation}
        onCancel={() => _setIsConfirmationModal(false)}
        title="Dados de pagamento registados"
        body="Os dados de pagamento foram registados com sucesso. 
                    Já podes começar a gerir as tuas reservas."
        confirmLabel="Começar a gerir"
        confirmColor="#ee754c"
        centered={true}
      />
    </Container>
  );
}

export default ChoosePlanAndPay;
